import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const privacyLinks = [
  { title: 'Privacy & Security explained', href: '#privacy-explained' },
  { title: 'How SweetPilot protects your data', href: '#data-protection' },
  { title: 'Your privacy rights', href: '#privacy-rights' },
];

const dataProtectionLinks = [
  { title: 'End-to-end encryption explained', href: '#encryption' },
  { title: 'How your data is stored and secured', href: '#data-storage' },
  { title: 'Data retention and deletion policies', href: '#data-retention' },
  { title: 'How we handle your Brain AI data', href: '#brain-ai-data' },
  { title: 'Third-party data sharing policy', href: '#third-party-sharing' },
];

const complianceLinks = [
  { title: 'GDPR compliance', href: '/gdpr' },
  { title: 'CCPA compliance', href: '#ccpa' },
  { title: 'SOC 2 Type II certification', href: '#soc2' },
  { title: 'ISO 27001 certification', href: '#iso27001' },
  { title: 'Data Processing Agreement (DPA)', href: '#dpa' },
];

const aiDataLinks = [
  { title: 'How AI processes your conversations', href: '#ai-processing' },
  { title: 'Is my data used to train AI models?', href: '#ai-training' },
  { title: 'AI data privacy safeguards', href: '#ai-privacy' },
  { title: 'Opting out of data processing', href: '#opt-out' },
];

const accountSecurityLinks = [
  { title: 'Setting up two-factor authentication (2FA)', href: '#2fa' },
  { title: 'Password requirements and best practices', href: '#passwords' },
  { title: 'Managing active sessions', href: '#sessions' },
  { title: 'Team access controls and permissions', href: '#access-controls' },
  { title: 'Suspicious activity alerts', href: '#suspicious-activity' },
];

const incidentLinks = [
  { title: 'How we handle security incidents', href: '#incident-response' },
  { title: 'Data breach notification policy', href: '#breach-notification' },
  { title: 'Security bug bounty program', href: '#bug-bounty' },
  { title: 'How to report a security vulnerability', href: '#report-vulnerability' },
];

const legalLinks = [
  { title: 'Privacy Policy', href: '/privacy' },
  { title: 'Terms of Service', href: '/terms' },
  { title: 'Cookie Policy', href: '/cookies' },
  { title: 'GDPR', href: '/gdpr' },
  { title: 'Refund Policy', href: '/refund-policy' },
];

const faqLinks = [
  { title: 'Privacy & Security FAQ', href: '#security-faq' },
  { title: 'Troubleshooting security issues', href: '#troubleshooting' },
];

interface LinkItemProps {
  title: string;
  href: string;
  isDark: boolean;
}

function LinkItem({ title, href, isDark }: LinkItemProps) {
  return (
    <Link
      to={href}
      className={`flex items-center justify-between py-4 px-2 transition-all group border-b ${
        isDark
          ? 'border-white/5 hover:bg-white/5'
          : 'border-slate-100 hover:bg-slate-50'
      }`}
    >
      <span className={`font-medium transition-colors ${
        isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
      }`}>
        {title}
      </span>
      <ChevronRight className={`w-5 h-5 transition-colors ${
        isDark ? 'text-gray-600 group-hover:text-purple-400' : 'text-slate-300 group-hover:text-purple-600'
      }`} />
    </Link>
  );
}

interface SectionCardProps {
  title: string;
  links: Array<{ title: string; href: string }>;
  isDark: boolean;
  delay?: number;
}

function SectionCard({ title, links, isDark, delay = 0 }: SectionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`rounded-2xl border overflow-hidden ${
        isDark
          ? 'bg-dark-800/50 border-white/5'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      <div className={`px-6 py-5 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
      </div>
      <div className="px-4">
        {links.map((link) => (
          <LinkItem
            key={link.title + link.href}
            title={link.title}
            href={link.href}
            isDark={isDark}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default function PrivacySecurityDocs() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title=""
      subtitle=""
      showBackButton={false}
    >
      {/* Breadcrumbs */}
      <nav className="mb-8">
        <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link
              to="/docs"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              All Collections
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            Privacy & Security
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <h1 className={`text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Privacy & Security
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Learn how we protect your data and privacy.
        </p>
      </motion.div>

      {/* Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`rounded-2xl border overflow-hidden mb-6 ${
          isDark
            ? 'bg-dark-800/50 border-white/5'
            : 'bg-white border-slate-200 shadow-sm'
        }`}
      >
        <div className="px-4">
          {privacyLinks.map((link) => (
            <LinkItem
              key={link.title}
              title={link.title}
              href={link.href}
              isDark={isDark}
            />
          ))}
        </div>
      </motion.div>

      {/* Data Protection */}
      <SectionCard
        title="Data Protection"
        links={dataProtectionLinks}
        isDark={isDark}
        delay={0.2}
      />

      <div className="h-6" />

      {/* Compliance & Certifications */}
      <SectionCard
        title="Compliance & Certifications"
        links={complianceLinks}
        isDark={isDark}
        delay={0.3}
      />

      <div className="h-6" />

      {/* AI & Data Processing */}
      <SectionCard
        title="AI & Data Processing"
        links={aiDataLinks}
        isDark={isDark}
        delay={0.4}
      />

      <div className="h-6" />

      {/* Account Security */}
      <SectionCard
        title="Account Security"
        links={accountSecurityLinks}
        isDark={isDark}
        delay={0.5}
      />

      <div className="h-6" />

      {/* Security Incidents */}
      <SectionCard
        title="Security Incidents & Reporting"
        links={incidentLinks}
        isDark={isDark}
        delay={0.6}
      />

      <div className="h-6" />

      {/* Legal Documents */}
      <SectionCard
        title="Legal Documents"
        links={legalLinks}
        isDark={isDark}
        delay={0.7}
      />

      <div className="h-6" />

      {/* FAQ & Troubleshooting */}
      <SectionCard
        title="Help & Support"
        links={faqLinks}
        isDark={isDark}
        delay={0.8}
      />

      <div className="h-16" />

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
        className="text-center"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🔒</span>
        </div>
        <a
          href="mailto:security@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          security@sweetpilot.com
        </a>
      </motion.div>
    </StaticPageLayout>
  );
}
